import { Dayjs } from "dayjs";
import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";

export interface ReliabilityPushLegStats {
    sent: number;
    failed: number;
    excluded: number;
    byError: Record<string, number>;
}

export interface ReliabilityPushLegacyCallStats {
    total: number;
    failed: number;
    byError: Record<string, number>;
}

export interface ReliabilityPushStats {
    legs: ReliabilityPushLegStats;
    legacyCalls: ReliabilityPushLegacyCallStats;
}

export interface LatencyStats {
    p50: number | null;
    p90: number | null;
}

export interface ReliabilityStats {
    push: ReliabilityPushStats;
    pushDispatchLatencyMs: LatencyStats;
    answerLatencyMs: LatencyStats;
    voicemailReasons: Record<string, number>;
    recordingPhases: Record<string, number>;
    resolutionStatus: Record<string, number>;
    faulty: { total: number; byReason: Record<string, number> };
}

const EMPTY: ReliabilityStats = {
    push: {
        legs: { sent: 0, failed: 0, excluded: 0, byError: {} },
        legacyCalls: { total: 0, failed: 0, byError: {} },
    },
    pushDispatchLatencyMs: { p50: null, p90: null },
    answerLatencyMs: { p50: null, p90: null },
    voicemailReasons: {},
    recordingPhases: {},
    resolutionStatus: {},
    faulty: { total: 0, byReason: {} },
};

/**
 * Fetches the Leedflow reliability stats from the backend (uses the Admin SDK
 * server-side, which bypasses Firestore security rules).
 */
async function getReliabilityStats(
    auth: Authentication | null,
    period?: [Dayjs | null, Dayjs | null],
    subscriptionIds?: string[],
    exclude?: string[],
): Promise<ReliabilityStats> {
    if (!auth) return EMPTY;

    const body: {
        period?: { from: number; to: number };
        subscriptionIds?: string[];
        exclude?: string[];
    } = {};

    if (period && period[0] && period[1]) {
        body.period = {
            from: period[0].startOf('day').toDate().getTime(),
            to: period[1].endOf('day').toDate().getTime(),
        };
    }
    if (subscriptionIds && subscriptionIds.length > 0) {
        body.subscriptionIds = subscriptionIds;
    }
    if (exclude && exclude.length > 0) {
        body.exclude = exclude;
    }

    const response = await HttpClient.post('/leedflow/reliability-stats', body, auth.token);

    // Même garde-fou que `getOverviewStats` : la route n'existe pas sur un
    // backend antérieur à cette refonte, et un front déployé en avance doit
    // afficher une erreur plutôt que planter.
    if (!response || !(response as ReliabilityStats).faulty) {
        throw new Error('Réponse /leedflow/reliability-stats inattendue : backend non à jour ?');
    }

    return response as ReliabilityStats;
}

export default getReliabilityStats;
